import React, { useState } from 'react';
import { FaRupeeSign, FaChartLine, FaCalendar, FaInfoCircle, FaShieldAlt, FaQuestionCircle, FaGem, FaMoneyBillWave, FaClock, FaChartPie, FaCalculator, FaChartArea } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';

const NPSCalculator = () => {
  const [monthlyContribution, setMonthlyContribution] = useState('');
  const [investmentPeriod, setInvestmentPeriod] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const calculateNPS = () => {
    if (!monthlyContribution || !investmentPeriod || !expectedReturn) {
      setError('Please fill in all required fields');
      return;
    }

    const P = parseFloat(monthlyContribution);
    const r = parseFloat(expectedReturn) / 100;
    const n = parseFloat(investmentPeriod);

    if (isNaN(P) || isNaN(r) || isNaN(n)) {
      setError('Please enter valid numbers');
      return;
    }

    // Calculate future value using compound interest formula
    const futureValue = P * ((Math.pow(1 + r/12, n*12) - 1) / (r/12)) * (1 + r/12);
    
    const resultData = {
      futureValue: futureValue.toFixed(2),
      totalInvestment: (P * n * 12).toFixed(2),
      totalGains: (futureValue - (P * n * 12)).toFixed(2)
    };

    setResult(resultData);
    setError('');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const chartOptions = {
    chart: {
      type: 'area',
      height: 400,
      toolbar: {
        show: false
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      }
    },
    colors: ['#FFB703', '#FF8500'],
    stroke: {
      width: 3,
      curve: 'smooth',
      lineCap: 'round'
    },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2,
        stops: [0, 90, 100]
      }
    },
    xaxis: {
      categories: Array.from({ length: result ? parseFloat(investmentPeriod) : 0 }, (_, i) => i + 1),
      title: {
        text: 'Years',
        style: {
          fontSize: '14px',
          fontWeight: 600,
          color: '#4B5563'
        }
      },
      labels: {
        style: {
          colors: '#6B7280',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif'
        }
      }
    },
    yaxis: {
      title: {
        text: 'Amount (₹)',
        style: {
          fontSize: '14px',
          fontWeight: 600,
          color: '#4B5563'
        }
      },
      labels: {
        style: {
          colors: '#6B7280',
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif'
        },
        formatter: function (val) {
          return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
          }).format(val);
        }
      }
    },
    legend: {
      position: 'top',
      fontSize: '14px',
      fontFamily: 'Inter, sans-serif',
      labels: {
        colors: '#4B5563'
      }
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function (val) {
          return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
          }).format(val);
        }
      },
      theme: 'dark',
      style: {
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif'
      }
    },
    grid: {
      show: true,
      borderColor: '#E5E7EB',
      strokeDashArray: 4,
      position: 'back'
    }
  };

  const chartSeries = result ? [
    {
      name: 'Total Investment',
      data: Array.from({ length: result ? parseFloat(investmentPeriod) : 0 }, (_, i) => 
        parseFloat(monthlyContribution) * (i + 1) * 12
      )
    },
    {
      name: 'Future Value',
      data: Array.from({ length: result ? parseFloat(investmentPeriod) : 0 }, (_, i) => 
        parseFloat(monthlyContribution) * ((Math.pow(1 + parseFloat(expectedReturn)/100/12, (i + 1)*12) - 1) / (parseFloat(expectedReturn)/100/12)) * (1 + parseFloat(expectedReturn)/100/12)
      )
    }
  ] : [];

  return (
    <div className="bg-gradient-to-br from-yellow-50 to-orange-50 min-h-screen p-8">
      <Helmet>
        <title>NPS Calculator - National Pension Scheme Calculator</title>
        <meta name="description" content="Calculate your NPS returns and plan your retirement savings." />
        <meta property="og:title" content="NPS Calculator - National Pension Scheme Calculator" />
        <meta property="og:description" content="Calculate your NPS returns and plan your retirement savings." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-white rounded-3xl shadow-2xl p-10 max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-10">
          <h2 className="text-4xl font-bold text-gray-800 flex items-center">
            <FaGem className="mr-3 text-4xl text-yellow-600" />
            NPS Calculator
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl shadow-md">
            <label className="block text-yellow-700 text-sm font-bold mb-3 flex items-center">
              <FaMoneyBillWave className="inline-block mr-2 text-yellow-500" />
              Monthly Contribution (₹)
            </label>
            <div className="relative">
              <FaRupeeSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-yellow-400" />
              <input
                type="number"
                value={monthlyContribution}
                onChange={(e) => setMonthlyContribution(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-yellow-200 rounded-lg focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-all duration-300"
                placeholder="Enter monthly contribution"
                min="0"
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl shadow-md">
            <label className="block text-yellow-700 text-sm font-bold mb-3 flex items-center">
              <FaClock className="inline-block mr-2 text-yellow-500" />
              Investment Period (Years)
            </label>
            <input
              type="number"
              value={investmentPeriod}
              onChange={(e) => setInvestmentPeriod(e.target.value)}
              className="w-full p-3 border border-yellow-200 rounded-lg focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-all duration-300"
              placeholder="Enter investment period"
              min="0"
            />
          </div>

          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl shadow-md">
            <label className="block text-yellow-700 text-sm font-bold mb-3 flex items-center">
              <FaChartPie className="inline-block mr-2 text-yellow-500" />
              Expected Return Rate (% p.a.)
            </label>
            <input
              type="number"
              value={expectedReturn}
              onChange={(e) => setExpectedReturn(e.target.value)}
              className="w-full p-3 border border-yellow-200 rounded-lg focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200 transition-all duration-300"
              placeholder="Enter expected return rate"
              min="0"
              step="0.01"
            />
          </div>
        </div>

        <button
          onClick={calculateNPS}
          className="w-full bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-black font-bold py-4 px-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 mb-10"
        >
          <FaCalculator className="inline-block mr-2" />
          Calculate NPS Returns
        </button>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-6 py-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartArea className="mr-3 text-yellow-600" />
              Growth Comparison Graph
            </h2>
            <div className="bg-white p-6 rounded-xl shadow-md">
              <Chart options={chartOptions} series={chartSeries} type="area" height={400} />
            </div>
          </div>
        )}

        {result && (
          <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <FaChartLine className="mr-3 text-yellow-600" />
              NPS Returns Analysis
            </h2>
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Investment</span>
                <span className="font-semibold text-yellow-600">
                  {formatCurrency(result.totalInvestment)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Gains</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(result.totalGains)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Future Value</span>
                <span className="font-semibold text-yellow-600">
                  {formatCurrency(result.futureValue)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfoCircle className="mr-3 text-yellow-600" />
            About NPS
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-yellow-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-yellow-700">What is NPS?</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Government-backed pension scheme</li>
                <li>Tax benefits under Section 80CCD</li>
                <li>Choice between Active and Auto choice schemes</li>
                <li>Flexible investment options</li>
              </ul>
            </div>
            <div className="p-6 bg-yellow-50 rounded-xl">
              <h3 className="text-xl font-semibold mb-4 text-yellow-700">Benefits</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2">
                <li>Long-term wealth creation</li>
                <li>Retirement security</li>
                <li>Tax advantages</li>
                <li>Professional fund management</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg mb-8">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaQuestionCircle className="mr-3 text-yellow-600" />
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-yellow-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-yellow-700">What is the minimum contribution?</h3>
              <p className="text-gray-600">
                The minimum contribution is ₹500 per year or ₹100 per quarter.
              </p>
            </div>
            <div className="bg-yellow-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-yellow-700">Who can invest in NPS?</h3>
              <p className="text-gray-600">
                Indian citizens aged 18-60 can invest in NPS.
              </p>
            </div>
            <div className="bg-yellow-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-yellow-700">Is NPS tax-free?</h3>
              <p className="text-gray-600">
                Yes, NPS offers tax benefits under Section 80CCD(1) and 80CCD(2).
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl shadow-lg">
          <h2 className="text-2xl font-semibold mb-6 flex items-center">
            <FaInfoCircle className="mr-3 text-yellow-600" />
            How to Use
          </h2>
          <div className="space-y-6">
            <div className="bg-yellow-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-yellow-700">Step 1: Enter Your Monthly Contribution</h3>
              <p className="text-gray-600">
                Input the amount you plan to contribute monthly to your NPS account.
              </p>
            </div>
            <div className="bg-yellow-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-yellow-700">Step 2: Set Investment Period</h3>
              <p className="text-gray-600">
                Enter the number of years you plan to invest in NPS.
              </p>
            </div>
            <div className="bg-yellow-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-yellow-700">Step 3: Enter Expected Return Rate</h3>
              <p className="text-gray-600">
                Input your expected annual return rate (typically between 8-12%).
              </p>
            </div>
            <div className="bg-yellow-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold mb-3 text-yellow-700">Step 4: Calculate Returns</h3>
              <p className="text-gray-600">
                Click the "Calculate NPS Returns" button to see your potential returns.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NPSCalculator;
